import React, { useState } from "react";
import {
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Group, GroupType } from "../../lib/types";
import GroupTypeSelector from "./GroupTypeSelector";

interface GroupSettingsProps {
  group: Group;
  onSave: (payload: Partial<Group>) => Promise<void> | void;
  onDelete?: () => void;
  loading?: boolean;
}

const GroupSettings: React.FC<GroupSettingsProps> = ({
  group,
  onSave,
  onDelete,
  loading = false,
}) => {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || "");
  const [type, setType] = useState<GroupType>(group.type);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    console.log("Saving group settings", { groupId: group.id, name, type });
    await onSave({
      name: name.trim(),
      description: description.trim() || null,
      type,
    });
  };

  return (
    <Card elevation={0} sx={{ borderRadius: 1 }}>
      <CardContent>
        <Stack spacing={3} component="form" onSubmit={handleSubmit}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>
              Group settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update details for this group.
            </Typography>
          </Stack>

          <TextField
            label="Group name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
          />

          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <GroupTypeSelector value={type} onChange={setType} />

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Tooltip title="Save changes">
              <span>
                <IconButton type="submit" color="success" disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <SaveIcon />}
                </IconButton>
              </span>
            </Tooltip>
            {onDelete && (
              <Tooltip title="Delete group">
                <span>
                  <IconButton
                    color="error"
                    onClick={() => {
                      console.log("Deleting group", { groupId: group.id });
                      onDelete();
                    }}
                    disabled={loading}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default GroupSettings;
